import RestaurantBoxGenerated, { RestaurantBoxGeneratedEntityManager } from './generated/RestaurantBox.generated';
import { BusinessEntityFieldType } from '@xFrame4/business/base/Constants';
import { imageInfoGraphQlDetails } from '@xFrame4/business/Constants';
import { YouTubeVideoInfo, ImageInfo } from '@xFrame4/business/Types';
import ImageGallery, { GalleryImage } from '@xFrame4/business/images/ImageGallery';
import { BusinessEntityWithOrderNoHelper } from '@xFrame4/business/base/BusinessEntityWithOrderNoHelper';
import { RestaurantBoxTranslation } from './RestaurantBoxTranslation';
import { BusinessEntityId, BusinessEntityFilter } from '@xFrame4/business/base/BusinessEntity';
import RestaurantMenu from './RestaurantMenu';

/**
 * Optimized GraphQL query for gallery images used in detail pages.
 * Loads the necessary image sizes:
 * - original: for lightbox full view
 * - thumbnail: for lightbox thumbnails
 * - veryLarge169: for the main preview image (16:9 aspect ratio)
 * - large169: for hero section
 */
const galleryImagesDetailQuery = `
    id
    orderNo
    isDefault
    original { ${imageInfoGraphQlDetails} }
    thumbnail { ${imageInfoGraphQlDetails} }
    veryLarge169 { ${imageInfoGraphQlDetails} }
    large169 { ${imageInfoGraphQlDetails} }
`;

/**
 * Minimal GraphQL query for gallery images used in listings.
 * Only loads the medium size for preview thumbnails.
 */
const galleryImagesListQuery = `
    id
    orderNo
    isDefault
    medium { ${imageInfoGraphQlDetails} }
`;

export class RestaurantBoxEntityManager extends RestaurantBoxGeneratedEntityManager<RestaurantBoxTranslation, RestaurantBox>
{
    constructor()
    {
        super({
            createEntity: () => new RestaurantBox(),
            fields: [
                { name: 'galleryImages', type: BusinessEntityFieldType.Custom, isInput: false, customGraphQl: ImageGallery.graphQlGalleryImageFieldDetails },
                { 
                    name: 'attachments', 
                    type: BusinessEntityFieldType.Custom, 
                    isInput: false, 
                    customGraphQl: `
                        id
                        fileName
                        name
                        description
                        isActive
                        url
                        translations {
                            language
                            name
                            description
                        }
                    ` 
                },
            ]
        });
    }
}


export class RestaurantBox extends RestaurantBoxGenerated<RestaurantBoxTranslation>
{
    static _manager: RestaurantBoxEntityManager;
    static get manager(): RestaurantBoxEntityManager
    {
        if (!this._manager) this._manager = new RestaurantBoxEntityManager();
        return this._manager;
    }

    imageGallery: ImageGallery;
    galleryImages: GalleryImage[] = [];
    attachments: any[] = [];

    constructor()
    {
        super();
        this.imageGallery = new ImageGallery(this, 'RestaurantBoxImageGallery');
    }

    // Override
    copyFromGraphQL(graphQlObject: any)
    {
        super.copyFromGraphQL(graphQlObject);

        if (graphQlObject.galleryImages != null)
        {
            this.imageGallery.loadImagesFromGraphQl(graphQlObject.galleryImages);
        }
        
        // Load attachments
        if (graphQlObject.attachments != null)
        {
            this.attachments = graphQlObject.attachments;
        }
    }

    // Optional: override the toJson method to skip the gallery images by setting the skipFields property (it's an array of field names)
    // This is useful when working Next.js (cannot serialize the gallery images)
    toJson()
    {
        return super.toJson(['imageGallery']);
    }

    /**
     * Move the restaurant box to a new position.
     * 
     * @param targetObject The target object to move the restaurant box to.
     * @param before If true, the restaurant box will be moved before the target object. If false, the restaurant box will be moved after the target object.
     */
    async moveTo(targetObject: RestaurantBox, before: boolean = true)
    {
        return BusinessEntityWithOrderNoHelper.moveTo('RestaurantBox', this, targetObject, before);
    }

    /**
     * Load a light version of a restaurant box.
     * 
     * @param filter Optional filter to narrow down the results
     */
    static async loadLight(filter?: BusinessEntityFilter)
    {
        let query = `
            id
            translations {
                language
                title
                slug
                shortDescription
                description
                info
            }
            restaurantMenu {
                id
                translations {
                    language
                    name
                }
            }
            galleryImages {
                ${galleryImagesDetailQuery}
            }
            attachments {
                id
                fileName
                name
                description
                isActive
                url
                translations {
                    language
                    name
                    description
                }
            }
        `;

        return await this.manager.loadCustom(query, filter);
    }

    /**
     * Load restaurant boxes with minimal fields.
     * 
     * @param filter Optional filter to narrow down the results
     */
    static async loadVeryLight(filter?: BusinessEntityFilter)
    {
        let query = `
            id
            orderNo
            isPromoted
            translations {
                language
                title
                slug
                shortDescription
            }
            galleryImages (isDefault: true) {
                ${galleryImagesListQuery}
            }
        `;

        return await this.manager.loadCustom(query, filter);
    }

    /**
     * Get a very light version of a restaurant box for navigation and routing.
     * 
     * @param language The translation language
     * @param slug The slug for the language
     */
    static async getVeryLightForNavigation(language: string, slug: string)
    {
        let query = `
            id
            translations {
                language
                slug
                title
                shortDescription
                seoTitle
                seoDescription
                seoKeywords
            }
        `;

        let result = await this.manager.loadCustom(query, {
            translation_Language: language,
            translation_Slug: slug
        });

        return result?.entities[0] as RestaurantBox | null;
    }
}

/**
 * Light version of RestaurantBox for optimized loading
 */
export type RestaurantBoxLight = {
    id: BusinessEntityId;
    translations: RestaurantBoxTranslationLight[];
    galleryImages: GalleryImage[];
    attachments: RestaurantBoxAttachmentLight[];
    restaurantMenu?: RestaurantMenuLight;
}

export type RestaurantBoxTranslationLight = {
    language: string;
    title: string;
    slug: string;
    shortDescription: string;
    description: string;
    info: string;
}

export type RestaurantBoxAttachmentLight = {
    id: BusinessEntityId;
    fileName: string;
    name: string;
    description: string;
    isActive: boolean;
    url: string;
    translations: RestaurantBoxAttachmentTranslationLight[];
}

export type RestaurantBoxAttachmentTranslationLight = {
    language: string;
    name: string;
    description: string;
}

export type RestaurantMenuLight = {
    id: BusinessEntityId;
    translations: RestaurantMenuTranslationLight[];
}

export type RestaurantMenuTranslationLight = {
    language: string;
    name: string;
}

export default RestaurantBox;